import { useState } from "react";
import { ArrowRight, Check, Crown, Sparkles } from "lucide-react";
import { Spinner } from "@/components/Common";
import { cn } from "@/lib/utils";
import { humanize } from "@/lib/api";
import { planCost, planId, yearlyLive } from "@/lib/plans";
import { PlanDetails, featureCount } from "@/components/login/PlanDetails";
import { RecruiterPerk } from "@/components/recruiter/RecruiterPerk";

const PlanCard = ({ p, current, yearly, onOpen }) => {
  const id = planId(p);
  const hot = id === "elite";
  const cost = planCost(p, yearly);
  return (
    <button onClick={onOpen} data-testid={`plan-${id}`} aria-label={`See ${p.name || id} plan details`}
      className={cn("fade-up group relative flex flex-col rounded-2xl border bg-[#18181B] p-6 text-left transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1",
        current ? "border-emerald-500/50" : hot ? "border-[#D4AF37]/60 shadow-[0_0_60px_-20px_rgba(212,175,55,0.45)]" : "border-[#27272A] hover:border-[#D4AF37]/50")}>
      {current ? <span className="absolute -top-3 left-6 flex items-center gap-1 rounded-full bg-emerald-500 px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider text-black" data-testid={`plan-current-${id}`}><Check className="h-3 w-3" strokeWidth={3} />Current plan</span>
        : (p.badge || hot) && <span className="absolute -top-3 left-6 flex items-center gap-1 rounded-full bg-[#D4AF37] px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider text-black" data-testid={`plan-badge-${id}`}><Sparkles className="h-3 w-3" />{p.badge || "Most powerful"}</span>}
      <h3 className="flex items-center gap-2 font-display text-2xl text-zinc-100">{hot && <Crown className="h-5 w-5 text-[#D4AF37]" />}{p.name || p.title || humanize(id)}</h3>
      <div className="mt-3 flex items-baseline gap-1"><span className="font-display text-4xl font-semibold text-zinc-100" data-testid={`plan-price-${id}`}>{cost.price}</span><span className="text-zinc-500">/{cost.unit}</span></div>
      {cost.note && <p className="mt-1 text-xs font-medium text-emerald-400" data-testid={`plan-save-${id}`}>{cost.note}</p>}
      <RecruiterPerk id={id} />
      <ul className="mt-4 flex-1 space-y-2">
        {(p.features || p.perks || []).slice(0, 3).map((t) => <li key={t} className="flex gap-2 text-sm text-zinc-300"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" />{t}</li>)}
      </ul>
      <span className="mt-6 flex items-center justify-between rounded-full border border-[#D4AF37]/40 bg-[#33290D]/60 px-4 py-2 text-sm font-semibold text-[#F3C649] transition-colors group-hover:bg-[#D4AF37] group-hover:text-black" data-testid={`plan-open-${id}`}>
        <span>{current ? "View details" : `See all ${featureCount(p)} features`}</span><ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </span>
    </button>
  );
};

const Footer = ({ p, current, included, yearly, busy, onBuy }) => {
  const id = planId(p);
  const blocked = yearly && id !== "free" && !yearlyLive(p);
  const label = current ? "Current plan" : id === "free" ? "Included with every account" : included ? "Included in your plan" : blocked ? "Yearly billing coming soon" : `Upgrade to ${p.name || humanize(id)}${yearly ? " yearly" : ""}`;
  const off = current || included || id === "free" || blocked || !!busy;
  return (
    <>
      <button onClick={() => onBuy(id)} disabled={off} data-testid="plan-details-checkout"
        className={cn("relative flex w-full items-center justify-center overflow-hidden rounded-full px-6 py-3 text-sm font-semibold transition-shadow",
          off ? "cursor-not-allowed border border-[#27272A] bg-[#18181B] text-zinc-400" : "lp-shine bg-gradient-to-r from-[#D4AF37] via-[#F3C649] to-[#D4AF37] text-black shadow-[0_0_30px_rgba(212,175,55,0.3)] hover:shadow-[0_0_40px_rgba(243,198,73,0.5)]")}>
        {busy === id ? <Spinner /> : label}
      </button>
      <p className="mt-2 text-center text-[11px] text-zinc-500" data-testid="plan-details-footnote">
        {blocked && !included ? "Switch to Monthly to subscribe today." : included ? "Your current plan already includes all of this." : current ? "You're on this plan. Manage it from the top of this page." : "Secure checkout with Stripe. Cancel any time."}
      </p>
    </>
  );
};

export const UpgradePlans = ({ plans, yearly, status, busy, onBuy, initialOpen }) => {
  const isCurrent = (id) => status(id) === "current";
  const [open, setOpen] = useState(() => (plans.some((p) => planId(p) === initialOpen) ? initialOpen : null));
  return (
    <>
      <div className={cn("grid gap-6 md:grid-cols-2 xl:max-w-5xl", plans.length >= 3 && "xl:grid-cols-3")} data-testid="upgrade-plans">
        {plans.map((p) => <PlanCard key={planId(p)} p={p} yearly={yearly} current={isCurrent(planId(p))} onOpen={() => setOpen(planId(p))} />)}
      </div>
      <p className="mt-3 text-xs text-zinc-500">Tap a plan to see everything it includes and compare plans side by side.</p>
      {open && <PlanDetails plans={plans} active={open} yearly={yearly} onPick={setOpen} onClose={() => setOpen(null)}
        footer={(p) => <Footer p={p} yearly={yearly} busy={busy} current={isCurrent(planId(p))} included={status(planId(p)) === "included"} onBuy={onBuy} />} />}
    </>
  );
};
